import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from './AuthContext';

const CompanyContext = createContext();

const EMPTY = {
  name: '',
  tagline: '',
  email: '',
  phone: '',
  website: '',
  gstin: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
  contactName: '',
  users: '',
  logo: '',
  services: [],
  bank: { accName: '', accNumber: '', bank: '', branch: '', ifsc: '' },
};

// The company profile is per-company (per org) and lives on the server, scoped to the
// logged-in workspace. It is NEVER read from browser storage, so editing one company's
// profile can never affect another company's account.
export function CompanyProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [company, setCompany] = useState(EMPTY);

  // One-time cleanup of the old browser-stored profile that used to bleed across accounts.
  useEffect(() => {
    try {
      localStorage.removeItem('ryzenos_company_profile');
      localStorage.removeItem('ryzenos_company');
    } catch {}
  }, []);

  const normalize = (c) => ({
    ...EMPTY,
    ...(c || {}),
    bank: { ...EMPTY.bank, ...((c && c.bank) || {}) },
    services: Array.isArray(c && c.services) ? c.services : [],
  });

  // Load this org's profile whenever the signed-in user changes (login / account switch).
  useEffect(() => {
    if (!api.getToken() || !isAuthenticated) { setCompany(EMPTY); return; }
    api.settings.get()
      .then(d => setCompany(normalize(d.company)))
      .catch(() => {});
  }, [user?.id, isAuthenticated]);

  const isComplete = !!(company.name && company.email && company.phone && company.address);
  const update = (data) => setCompany(prev => ({ ...prev, ...data }));

  // Persist the full profile for THIS org.
  const save = useCallback((data) => {
    const next = normalize(data);
    setCompany(next);
    return api.settings.save({ company: next }).catch(() => {});
  }, []);

  return (
    <CompanyContext.Provider value={{ company, update, save, isComplete }}>
      {children}
    </CompanyContext.Provider>
  );
}

export const useCompany = () => useContext(CompanyContext);
